import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { productCatalogAPI } from '../lib/api';
import {
  FileText, Megaphone, Layers, Plus, Edit, Save, X, Trash2, Loader2, Check, Euro,
} from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};
const itemVariants = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };

const OFFERS = [
  { key: 'contenido', title: 'Oferta Contenido', subtitle: 'Orgánico', icon: FileText, accent: 'violet' },
  { key: 'ads', title: 'Oferta Ads', subtitle: 'Publicidad', icon: Megaphone, accent: 'amber' },
  { key: 'completa', title: 'Oferta Completa', subtitle: 'Pack Contenido + Ads', icon: Layers, accent: 'emerald' },
];

const accentClasses = {
  violet: { ring: 'border-violet-200', head: 'text-violet-700', icon: 'bg-violet-100 text-violet-700', chip: 'bg-violet-50 text-violet-700' },
  amber: { ring: 'border-amber-200', head: 'text-amber-700', icon: 'bg-amber-100 text-amber-700', chip: 'bg-amber-50 text-amber-700' },
  emerald: { ring: 'border-emerald-300 ring-1 ring-emerald-200', head: 'text-emerald-700', icon: 'bg-emerald-100 text-emerald-700', chip: 'bg-emerald-50 text-emerald-700' },
};

const emptyOffer = () => ({ nombre: '', descripcion: '', incluye: [], precio: '', notas: '' });

function OfferCard({ meta, offer, canEdit, onSave, saving }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(offer || emptyOffer());
  const a = accentClasses[meta.accent];
  const Icon = meta.icon;

  useEffect(() => { if (!editing) setDraft(offer || emptyOffer()); }, [offer, editing]);

  const isEmpty = !offer?.nombre && !offer?.descripcion && !(offer?.incluye?.length) && !offer?.precio && !offer?.notas;

  const start = () => { setDraft(offer || emptyOffer()); setEditing(true); };
  const cancel = () => { setDraft(offer || emptyOffer()); setEditing(false); };
  const save = async () => {
    const clean = { ...draft, incluye: (draft.incluye || []).map((s) => s.trim()).filter(Boolean) };
    await onSave(meta.key, clean);
    setEditing(false);
  };

  const updateIncluye = (idx, val) => {
    const next = [...(draft.incluye || [])];
    next[idx] = val;
    setDraft({ ...draft, incluye: next });
  };
  const addIncluye = () => setDraft({ ...draft, incluye: [...(draft.incluye || []), ''] });
  const removeIncluye = (idx) => setDraft({ ...draft, incluye: (draft.incluye || []).filter((_, i) => i !== idx) });

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="h-full">
      <Card className={`h-full flex flex-col ${a.ring}`} data-testid={`offer-card-${meta.key}`}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-3">
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${a.icon}`}>
                <Icon className="w-5 h-5" />
              </div>
              <div>
                <CardTitle className={`text-base ${a.head}`}>{meta.title}</CardTitle>
                <p className="text-xs text-muted-foreground">{meta.subtitle}</p>
              </div>
            </div>
            {canEdit && !editing && (
              <Button variant="ghost" size="sm" onClick={start} data-testid={`edit-offer-${meta.key}`}>
                <Edit className="w-4 h-4 mr-1" /> Editar
              </Button>
            )}
          </div>
        </CardHeader>

        <CardContent className="flex-1 flex flex-col gap-4">
          {editing ? (
            <>
              <div>
                <Label className="text-xs">Nombre de la oferta</Label>
                <Input value={draft.nombre} onChange={(e) => setDraft({ ...draft, nombre: e.target.value })} placeholder="Nombre..." data-testid={`offer-nombre-${meta.key}`} />
              </div>
              <div>
                <Label className="text-xs">Descripción</Label>
                <Textarea rows={2} value={draft.descripcion} onChange={(e) => setDraft({ ...draft, descripcion: e.target.value })} placeholder="Descripción breve..." data-testid={`offer-descripcion-${meta.key}`} />
              </div>
              <div>
                <Label className="text-xs">Qué incluye</Label>
                <div className="space-y-2 mt-1">
                  {(draft.incluye || []).map((line, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Input value={line} onChange={(e) => updateIncluye(idx, e.target.value)} placeholder="Entregable..." data-testid={`offer-incluye-${meta.key}-${idx}`} />
                      <Button variant="ghost" size="icon" className="shrink-0" onClick={() => removeIncluye(idx)} data-testid={`remove-incluye-${meta.key}-${idx}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={addIncluye} data-testid={`add-incluye-${meta.key}`}>
                    <Plus className="w-4 h-4 mr-1" /> Añadir línea
                  </Button>
                </div>
              </div>
              <div>
                <Label className="text-xs">Precio</Label>
                <Input value={draft.precio} onChange={(e) => setDraft({ ...draft, precio: e.target.value })} placeholder="Ej: 600€/mes" data-testid={`offer-precio-${meta.key}`} />
              </div>
              <div>
                <Label className="text-xs">Notas</Label>
                <Textarea rows={2} value={draft.notas} onChange={(e) => setDraft({ ...draft, notas: e.target.value })} placeholder="Notas internas / condiciones..." data-testid={`offer-notas-${meta.key}`} />
              </div>
              <div className="flex gap-2 mt-auto pt-2">
                <Button onClick={save} disabled={saving} className="flex-1" data-testid={`save-offer-${meta.key}`}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Guardar
                </Button>
                <Button variant="outline" onClick={cancel}><X className="w-4 h-4" /></Button>
              </div>
            </>
          ) : isEmpty ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center py-8 text-muted-foreground">
              <Icon className="w-8 h-8 mb-2 opacity-40" />
              <p className="text-sm">Sin definir todavía</p>
              {canEdit && <p className="text-xs">Haz clic en "Editar" para rellenar esta oferta.</p>}
            </div>
          ) : (
            <>
              {offer.nombre && <h3 className="text-lg font-semibold leading-tight">{offer.nombre}</h3>}
              {offer.descripcion && <p className="text-sm text-muted-foreground">{offer.descripcion}</p>}
              {offer.precio && (
                <div className={`inline-flex items-center gap-1 self-start px-3 py-1.5 rounded-lg font-semibold ${a.chip}`} data-testid={`offer-precio-view-${meta.key}`}>
                  <Euro className="w-4 h-4" /> {offer.precio}
                </div>
              )}
              {offer.incluye?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Qué incluye</p>
                  <ul className="space-y-1.5">
                    {offer.incluye.map((line, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <Check className={`w-4 h-4 mt-0.5 shrink-0 ${a.head}`} />
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {offer.notas && (
                <div className="mt-auto pt-3 border-t">
                  <p className="text-xs text-muted-foreground italic">{offer.notas}</p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}

export function ProductPage() {
  const [catalog, setCatalog] = useState({ contenido: emptyOffer(), ads: emptyOffer(), completa: emptyOffer() });
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await productCatalogAPI.get();
      setCatalog(res.data || { contenido: emptyOffer(), ads: emptyOffer(), completa: emptyOffer() });
      setCanEdit(!!res.can_edit);
    } catch (e) {
      toast.error(e.message || 'Error al cargar el catálogo');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (key, offer) => {
    setSavingKey(key);
    try {
      const res = await productCatalogAPI.save({ [key]: offer });
      setCatalog(res.data);
      toast.success('Oferta guardada');
    } catch (e) {
      toast.error(e.message || 'Error al guardar');
      throw e;
    }
    setSavingKey(null);
  };

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-6" data-testid="product-page">
      <motion.div variants={itemVariants}>
        <h1 className="text-3xl font-bold tracking-tight">Producto</h1>
        <p className="text-muted-foreground mt-1">
          Catálogo de ofertas de la agencia. {canEdit ? 'Edítalo cuando cambien tus servicios o precios.' : 'Solo las CEOs pueden editarlo.'}
        </p>
      </motion.div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando catálogo...
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
          {OFFERS.map((meta) => (
            <OfferCard
              key={meta.key}
              meta={meta}
              offer={catalog[meta.key]}
              canEdit={canEdit}
              onSave={handleSave}
              saving={savingKey === meta.key}
            />
          ))}
        </div>
      )}
    </motion.div>
  );
}

export default ProductPage;
